import type { StayPlanAlertKind } from "@/lib/daycare-hotel/stayPlans";

export interface StayPlanAlertStorage {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem?: (key: string) => void;
  key?: (index: number) => string | null;
  length?: number;
}

export type DismissibleStayPlanAlert = StayPlanAlertKind;

export const STAY_PLAN_ALERT_DISMISSAL_PREFIX = "stay-plan-alert-dismissal:";
export const STAY_PLAN_ALERT_DISMISSAL_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

type StayPlanAlertDismissalContext = {
  day: string;
  petId: string;
  contractId: string;
};

function contextPrefix({ day, petId, contractId }: StayPlanAlertDismissalContext): string {
  return `${STAY_PLAN_ALERT_DISMISSAL_PREFIX}${day}:${petId}:${contractId}:`;
}

export function stayPlanAlertDismissalKey(input: StayPlanAlertDismissalContext & { alert: DismissibleStayPlanAlert }): string {
  return `${contextPrefix(input)}${input.alert}`;
}

function readDismissedAt(raw: string | null): number | null {
  if (!raw) return null;
  if (raw === "1") return Number.NaN;

  try {
    const value = JSON.parse(raw) as { dismissedAt?: unknown };
    return typeof value.dismissedAt === "number" ? value.dismissedAt : null;
  } catch {
    return null;
  }
}

export function isStayPlanAlertDismissed(storage: StayPlanAlertStorage, key: string, now = Date.now()): boolean {
  const dismissedAt = readDismissedAt(storage.getItem(key));
  if (dismissedAt === null) return false;
  if (Number.isNaN(dismissedAt)) return true;
  return now - dismissedAt < STAY_PLAN_ALERT_DISMISSAL_MAX_AGE_MS;
}

export function dismissStayPlanAlert(storage: StayPlanAlertStorage, key: string, now = Date.now()): void {
  storage.setItem(key, JSON.stringify({ dismissedAt: now }));
}

function storageKeys(storage: StayPlanAlertStorage): string[] {
  if (typeof storage.length !== "number" || !storage.key) return [];
  return Array.from({ length: storage.length }, (_, index) => storage.key?.(index)).filter(
    (key): key is string => Boolean(key),
  );
}

export function clearExpiredStayPlanAlertDismissals(storage: StayPlanAlertStorage, now = Date.now()): void {
  if (!storage.removeItem) return;

  for (const key of storageKeys(storage)) {
    if (!key.startsWith(STAY_PLAN_ALERT_DISMISSAL_PREFIX)) continue;
    const dismissedAt = readDismissedAt(storage.getItem(key));
    if (dismissedAt === null || Number.isNaN(dismissedAt) || now - dismissedAt >= STAY_PLAN_ALERT_DISMISSAL_MAX_AGE_MS) {
      storage.removeItem(key);
    }
  }
}

export function clearStayPlanAlertDismissals(storage: StayPlanAlertStorage, context: StayPlanAlertDismissalContext): void {
  if (!storage.removeItem) return;
  const prefix = contextPrefix(context);

  for (const key of storageKeys(storage)) {
    if (key.startsWith(prefix)) storage.removeItem(key);
  }
}
